import {
  chakra,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Close, Info } from '@time-line/components/icons';
import { Fragment, useState } from 'react';
import type { JunipProductActiveTrait } from 'types/reviews';

import { ProductActiveTraitMeter } from './ProductActiveTraitMeter';
import { ProductActiveTraitTooltip } from './ProductActiveTraitTooltip';

export type ActiveTrait = JunipProductActiveTrait & { ratingAverage: number };

interface ProductActiveTraitsProps {
  traits?: ActiveTrait[];
}

const getTooltipTitle = (trait: ActiveTrait) =>
  `Average ${trait.name.toLowerCase()}`;
const getTooltipLabel = (trait: ActiveTrait) =>
  trait.labelLeft && trait.labelRight
    ? `Based on a scale from '${trait.labelLeft}' to '${trait.labelRight}'`
    : '';

export function ProductActiveTraits({ traits }: ProductActiveTraitsProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: 'md' }) || false;

  const [selectedTrait, setSelectedTrait] = useState<ActiveTrait | null>(null);

  return (
    <Grid
      templateColumns="1fr 25% auto"
      columnGap="4xs"
      rowGap="3xs"
      alignItems="center"
      width="100%"
    >
      {traits &&
        traits.length > 0 &&
        traits.map((trait) => {
          const label = getTooltipLabel(trait);

          const splitText = getTooltipTitle(trait).split(' ');
          const textWithIcon = splitText.map((word, i) =>
            i === splitText.length - 1 ? (
              <chakra.span key={word} whiteSpace="nowrap">
                {word}
                <Info marginLeft="5xs" marginBottom={{ base: '5xs', md: 0 }} />
              </chakra.span>
            ) : (
              `${word} `
            ),
          );

          return (
            <Fragment key={trait.id}>
              <GridItem>
                <ProductActiveTraitTooltip
                  isDisabled={isMobile || !label}
                  label={label}
                >
                  <Text
                    as="button"
                    type="button"
                    textAlign="left"
                    aria-haspopup={isMobile ? 'dialog' : undefined}
                    onClick={() => {
                      setSelectedTrait(trait);
                      onOpen();
                    }}
                    size="body3"
                    fontWeight="medium"
                    _hover={{
                      cursor: 'help',
                    }}
                  >
                    {textWithIcon}
                  </Text>
                </ProductActiveTraitTooltip>
              </GridItem>
              <GridItem justifyItems="end">
                <ProductActiveTraitMeter value={trait.ratingAverage} />
              </GridItem>
              <GridItem textAlign="right">
                <Text
                  size="detail"
                  sx={{
                    fontVariantNumeric: 'slashed-zero',
                  }}
                >
                  {`${trait.ratingAverage.toFixed(1)}/5`}
                </Text>
              </GridItem>
            </Fragment>
          );
        })}
      <Drawer
        isOpen={isMobile && !!selectedTrait && isOpen}
        placement="bottom"
        onClose={onClose}
      >
        <DrawerOverlay />
        {selectedTrait && (
          <DrawerContent>
            <DrawerBody
              padding="2xs"
              background="gray.50"
              display="flex"
              flexDirection="column"
              gap="2xs"
            >
              <HStack justifyContent="space-between">
                <Info />
                <IconButton
                  variant="unstyled"
                  aria-label="Close drawer"
                  icon={<Close width="sm" />}
                  onClick={onClose}
                  boxSize="xs"
                />
              </HStack>
              <VStack gap="4xs" alignItems="flex-start">
                <Text size="body3" fontWeight="medium">
                  {getTooltipTitle(selectedTrait)}
                </Text>
                <Text size="body3" color="gray.600">
                  {getTooltipLabel(selectedTrait)}
                </Text>
              </VStack>
            </DrawerBody>
          </DrawerContent>
        )}
      </Drawer>
    </Grid>
  );
}
